// Разметка приложения. Единственное место, где создаётся DOM каркаса.
//
// Модули, которые на этапе загрузки ищут элементы (controls, grid), вызывают
// ensureAppShell() первой строкой — так порядок импортов не может оставить
// их без DOM.

import { t } from '../i18n';

import { icon } from './dom';
import { initialPreferences } from './preferences';
import { renderKpiIconChoices } from './visual-catalog';

let mounted = false;

export function ensureAppShell(): void {
  if (mounted) return;
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) throw new Error('Корневой элемент #app не найден');
  app.innerHTML = `
  <div class="application-shell">
    <header class="topbar">
      <div class="brand-block">
        <div class="brand-mark">${icon('chart-column-increasing')}</div>
        <div>
          <div class="brand-title">Цифровая демографическая обсерватория</div>
        </div>
      </div>
      <div class="dashboard-toolbar">
        <button id="dashboard-manager-button" class="icon-button topbar-icon-button" type="button" data-tooltip="Дашборды" aria-label="Дашборды">${icon('layout-dashboard')}</button>
        <div class="dashboard-title-shell">
          <button id="dashboard-title-button" class="dashboard-title-button" type="button" data-tooltip="Переименовать дашборд" aria-label="Переименовать дашборд">
            <span id="dashboard-title-text" data-i18n-skip>Новый дашборд</span>
            <span class="dashboard-title-edit-icon">${icon('pencil')}</span>
          </button>
          <input id="dashboard-title-input" class="dashboard-title-input" maxlength="300" value="Новый дашборд" hidden />
        </div>
        <button id="dashboard-visibility" class="visibility-chip" type="button" hidden></button>
        <span id="dirty-indicator" class="save-state requires-sign-in">Не сохранён</span>
        <button id="view-mode-button" class="mode-option app-internal-state" type="button" aria-pressed="false" hidden>Просмотр</button>
        <button id="toggle-view-mode" class="icon-button topbar-icon-button requires-sign-in" type="button" data-tooltip="Просмотр" aria-label="Просмотр" aria-pressed="false">${icon('eye')}</button>
        <button id="show-palette" class="icon-button topbar-icon-button requires-sign-in widget-panel-button" type="button" data-tooltip="Виджет" aria-label="Виджет" aria-expanded="true">${icon('square-plus')}</button>
        <button id="toggle-builder" class="icon-button topbar-icon-button app-internal-state" type="button" data-tooltip="Свойства" aria-label="Свойства" aria-expanded="false" hidden>${icon('panel-right')}</button>
        <button id="toggle-filterbar" class="icon-button topbar-icon-button" type="button" data-tooltip="Фильтры" aria-label="Фильтры" aria-expanded="false" hidden>${icon('filter')}</button>
        <button id="compact-layout" class="icon-button topbar-icon-button requires-sign-in compact-button" type="button" data-tooltip="Автоконфигурация" aria-label="Автоконфигурация">${icon('wand-sparkles')}</button>
        <button id="refresh-all" class="icon-button topbar-icon-button" type="button" data-tooltip="Обновить данные" aria-label="Обновить данные">${icon('refresh-cw')}</button>
        <div class="sources-menu-shell requires-sign-in">
          <button id="sources-menu-button" class="icon-button topbar-icon-button" type="button" data-tooltip="Источники" aria-label="Источники" aria-expanded="false">${icon('database')}</button>
          <div id="sources-menu" class="sources-menu" hidden>
            <button id="sources-emiss-catalog" type="button">${icon('search')}<span>Каталог ЕМИСС</span></button>
            <button id="open-emiss-manager" type="button">${icon('sliders-horizontal')}<span>Диагностика ЕМИСС</span></button>
            <button id="sources-emiss-log" type="button">${icon('scroll-text')}<span>Журнал запросов</span></button>
          </div>
        </div>
        <button id="locale-toggle" class="icon-button topbar-icon-button locale-toggle-button" type="button" data-i18n-skip aria-label="${t(initialPreferences.locale, 'currentLanguage')}" data-tooltip="${t(initialPreferences.locale, 'currentLanguage')}">${icon('languages')}<span id="locale-toggle-code">${initialPreferences.locale.toUpperCase()}</span></button>
        <button id="settings-fab" class="icon-button topbar-icon-button" type="button" aria-label="${t(initialPreferences.locale, 'settings')}" aria-expanded="false" aria-controls="settings-popover" data-tooltip="${t(initialPreferences.locale, 'settings')}">${icon('settings')}</button>
        <button id="auto-builder-button" class="icon-button topbar-icon-button requires-sign-in" type="button" data-tooltip="Автоконструктор" aria-label="Автоконструктор">${icon('sparkles')}</button>
        <button id="new-dashboard" class="icon-button topbar-icon-button requires-sign-in" type="button" data-tooltip="Новый дашборд" aria-label="Новый дашборд">${icon('file-plus-2')}</button>
        <button id="save-dashboard" class="icon-button topbar-icon-button save-icon-button requires-sign-in" type="button" data-tooltip="Сохранить" aria-label="Сохранить">${icon('save')}</button>
        <div id="auth-area" class="auth-area" hidden>
          <button id="auth-sign-in" class="button button-secondary auth-sign-in" type="button">${icon('log-in')}<span>Войти</span></button>
          <div class="auth-user-shell">
            <button id="auth-user-button" class="auth-user-button" type="button" aria-haspopup="true" aria-expanded="false" aria-controls="auth-user-menu">
              ${icon('circle-user-round')}<span id="auth-user-name" class="auth-user-name" data-i18n-skip></span>
            </button>
            <div id="auth-user-menu" class="auth-user-menu" hidden>
              <div class="auth-user-card">
                <strong id="auth-user-menu-name" data-i18n-skip></strong>
                <span id="auth-user-menu-email" data-i18n-skip></span>
                <span id="auth-user-menu-role" class="auth-role-badge"></span>
              </div>
              <button id="auth-sign-out" type="button">${icon('log-out')}<span>Выйти</span></button>
            </div>
          </div>
        </div>
      </div>
    </header>
    <div id="auth-banner" class="auth-banner" role="status" hidden>
      ${icon('info')}
      <span id="auth-banner-text">Войдите, чтобы создавать свои дашборды</span>
      <button id="auth-banner-sign-in" class="button button-primary" type="button">Войти</button>
    </div>

    <main class="workspace">
      <aside class="palette-panel" aria-label="Мастер данных">
        <div class="palette-heading">
          <div>
            <h2 class="sr-only">Данные</h2>
          </div>
          <div class="palette-heading-actions">
            <button id="builder-help" class="icon-button builder-help-button" type="button" data-tooltip="Выберите показатель, территорию и тип виджета — дашборд загрузит доступные наблюдения." aria-label="Как создать виджет">?</button>
            <button id="toggle-palette" class="icon-button panel-toggle" type="button" data-tooltip="Закрыть панель" aria-label="Закрыть панель" aria-expanded="true">${icon('x')}</button>
          </div>
        </div>
        <form id="widget-form" class="data-builder-form">
          <details open>
            <summary>Данные</summary>
            <div class="form-section">
              <label class="field full-width">
                <span>Источник</span>
                <select id="source-select">
                  <option value="">Все источники</option>
                  <!-- Международная статистика скрыта, пока каталог не наполнен; опция остаётся,
                       чтобы виджеты шаблонов с показателями World Bank открывались в редакторе. -->
                  <option value="worldbank" hidden disabled>Международная статистика</option>
                  <option value="emiss">Региональная статистика</option>
                  <option value="bdmo">Муниципальная статистика</option>
                  <option value="settlements">Населённые пункты</option>
                </select>
              </label>
              <label class="field full-width" id="indicator-section-field">
                <span>Раздел</span>
                <select id="indicator-section-select">
                  <option value="">Все разделы</option>
                </select>
              </label>
              <label class="field full-width" id="indicator-search-field">
                <span>Поиск</span>
                <input id="indicator-search" type="search" autocomplete="off" placeholder="Название, ID, единица или раздел" />
              </label>
              <label class="field full-width" id="indicator-field">
                <span>Показатель</span>
                <select id="indicator-select" required></select>
                <small id="indicator-description" class="field-help"></small>
              </label>

              <section id="left-emiss-catalog-picker" class="left-emiss-catalog-picker full-width" hidden>
                <div class="left-emiss-catalog-heading">
                  <div>
                    <strong>Поиск показателей</strong>
                    <small id="left-emiss-catalog-status">Каталог ещё не загружен.</small>
                  </div>
                  <button id="left-emiss-catalog-refresh" class="icon-button" type="button" data-tooltip="Обновить каталог" aria-label="Обновить каталог">${icon('refresh-cw')}</button>
                </div>
                <label class="field full-width legacy-catalog-search" hidden>
                  <span>Найти показатель по названию или ID</span>
                  <input id="left-emiss-catalog-query" type="search" autocomplete="off" placeholder="Например: рождаемость, население или 62852" />
                </label>
                <label class="check-field left-emiss-catalog-toggle">
                  <input id="left-emiss-catalog-include-excluded" type="checkbox" />
                  <span>Показать скрытые/исключённые</span>
                </label>
                <div class="left-emiss-catalog-actions">
                  <button id="left-emiss-catalog-search" class="button button-primary" type="button">${icon('search')}<span>Найти</span></button>
                  <button id="left-emiss-open-manager" class="button button-secondary" type="button">${icon('sliders-horizontal')}<span>Диагностика</span></button>
                </div>
                <div id="left-emiss-selected" class="left-emiss-selected" hidden></div>
                <div id="left-emiss-catalog-results" class="emiss-catalog-results left-emiss-catalog-results"></div>
              </section>

              <section id="left-worldbank-catalog-picker" class="left-emiss-catalog-picker full-width" hidden>
                <div class="left-emiss-catalog-heading">
                  <div>
                    <strong>Поиск международных показателей</strong>
                    <small id="left-worldbank-catalog-status">Каталог ещё не загружен.</small>
                  </div>
                  <button id="left-worldbank-catalog-refresh" class="icon-button" type="button" data-tooltip="Обновить каталог международной статистики" aria-label="Обновить каталог международной статистики">${icon('refresh-cw')}</button>
                </div>
                <label class="field full-width legacy-catalog-search" hidden>
                  <span>Найти международный показатель</span>
                  <input id="left-worldbank-catalog-query" type="search" autocomplete="off" placeholder="population, fertility, GDP или SP.POP.TOTL" />
                </label>
                <div class="left-emiss-catalog-actions">
                  <button id="left-worldbank-catalog-search" class="button button-primary" type="button">${icon('search')}<span>Найти</span></button>
                </div>
                <div id="left-worldbank-selected" class="left-emiss-selected" hidden></div>
                <div id="left-worldbank-catalog-results" class="emiss-catalog-results left-emiss-catalog-results"></div>
              </section>
            </div>
          </details>

          <details id="filters-details">
            <summary>Фильтры</summary>
            <div class="form-section">
              <label class="field">
                <span>Территориальный уровень</span>
                <select id="territory-level">
                  <option value="federal_district">Федеральные округа</option>
                  <option value="region" selected>Субъекты РФ</option>
                  <option value="municipality">Муниципалитеты</option>
                </select>
              </label>

              <label class="field" id="district-filter-field">
                <span>Федеральный округ</span>
                <select id="district-filter"><option value="">Все округа</option></select>
              </label>

              <label class="field full-width" id="region-filter-field">
                <span>Субъект РФ</span>
                <select id="region-filter"><option value="">Все субъекты</option></select>
              </label>

              <label class="field full-width" id="territory-search-field" hidden>
                <span>Поиск населённого пункта</span>
                <input id="territory-search" type="search" autocomplete="off" />
              </label>

              <label class="field full-width">
                <span>Территории</span>
                <select id="territory-select" class="native-multi-select" multiple size="7" required></select>
                <div id="territory-choice-list" class="checkbox-choice-list" aria-label="Выбор территорий"></div>
              </label>
              <div class="selection-actions full-width">
                <button id="select-all-territories" class="text-button" type="button">Выбрать видимые</button>
                <button id="clear-territories" class="text-button" type="button">Снять выбор</button>
                <span id="territory-count"></span>
              </div>

              <label class="field full-width">
                <span class="field-header-row">
                  <span>Годы</span>
                  <span class="quick-year-buttons" aria-label="Быстрый выбор последних лет">
                    <button type="button" class="mini-chip" data-year-quick="5">5</button>
                    <button type="button" class="mini-chip" data-year-quick="10">10</button>
                    <button type="button" class="mini-chip" data-year-quick="15">15</button>
                    <button type="button" class="mini-chip" data-year-quick="20">20</button>
                  </span>
                </span>
                <select id="year-select" class="native-multi-select" multiple size="4" required></select>
                <div id="year-choice-list" class="checkbox-choice-list compact" aria-label="Выбор лет"></div>
                <small id="year-selection-hint" class="field-help auto-selection-hint"></small>
              </label>
              <div class="selection-actions full-width">
                <button id="select-all-years" class="text-button" type="button">Выбрать все</button>
                <button id="clear-years" class="text-button" type="button">Снять выбор</button>
              </div>

              <label class="field full-width" id="month-field" hidden>
                <span>Месяцы</span>
                <select id="month-select" multiple size="4"></select>
                <small class="field-help">Пустой выбор означает все доступные месяцы.</small>
              </label>

              <div id="period-semantics" class="period-semantics full-width" hidden></div>
              <div id="dimension-fields" class="dimension-fields full-width" hidden></div>
            </div>
          </details>

          <details open id="visualization-details">
            <summary>Тип визуализации</summary>
            <div class="form-section">
              <label class="field full-width visualization-select-field">
                <span>Тип визуализации</span>
                <select id="visualization-select" required></select>
              </label>
              <div id="visualization-palette" class="visualization-palette full-width" role="listbox" aria-label="Тип визуализации"></div>
            </div>
          </details>

          <div id="form-error" class="form-error" role="alert" hidden></div>
          <div class="widget-submit-row">
            <button id="submit-widget" class="button button-accent full-width" type="submit">${icon('plus')}<span>Добавить на дашборд</span></button>
            <button id="add-as-new-widget" class="button button-secondary full-width" type="button" hidden>${icon('copy-plus')}<span>Добавить как новый</span></button>
          </div>
        </form>

      </aside>

      <aside class="builder-panel" aria-label="Инспектор оформления">
        <div class="builder-heading">
          <div>
            <h1 id="builder-title" class="sr-only">Свойства</h1>
          </div>
          <div class="builder-heading-actions">
            <button id="reset-widget" class="icon-button" type="button" data-tooltip="Сбросить оформление" aria-label="Сбросить оформление">${icon('rotate-ccw')}</button>
            <button id="close-builder" class="icon-button builder-close-button" type="button" data-tooltip="Закрыть панель" aria-label="Закрыть панель оформления">${icon('panel-right-close')}</button>
          </div>
        </div>

        <div id="inspector-empty" class="inspector-empty">
          <div class="inspector-empty-icon">${icon('mouse-pointer-2')}</div>
          <strong>Выберите виджет</strong>
          <p>Нажмите на карточку в рабочей области, чтобы настроить цвета, шкалы, легенду и остальные параметры.</p>
        </div>

        <div id="appearance-inspector" class="appearance-inspector" hidden>
          <details open>
            <summary>Оформление</summary>
            <div class="form-section">
              <label class="field full-width">
                <span>Заголовок виджета</span>
                <input id="widget-title-input" maxlength="300" placeholder="Будет сформирован автоматически" />
              </label>
              <label class="field full-width">
                <span>Заголовок виджета (английский)</span>
                <input id="widget-title-en-input" maxlength="300" lang="en" placeholder="Будет сформирован автоматически" />
              </label>
              <label class="field">
                <span>Палитра</span>
                <select id="palette-select">
                  <option value="dashboard">Цвета дашборда</option>
                  <option value="blue">Синяя</option>
                  <option value="green">Зелёная</option>
                  <option value="teal">Бирюзовая</option>
                  <option value="orange">Оранжевая</option>
                  <option value="red">Красная</option>
                  <option value="purple">Фиолетовая</option>
                  <option value="viridis">Viridis</option>
                  <option value="cividis">Cividis · доступная</option>
                  <option value="grayscale">Монохромная</option>
                  <option value="diverging">Расходящаяся</option>
                </select>
              </label>
              <label class="field">
                <span>Знаков после запятой</span>
                <input id="decimals-input" type="number" min="0" max="6" value="1" />
              </label>
              <label class="check-field"><input id="legend-checkbox" type="checkbox" checked /> <span>Показывать легенду</span></label>
              <label class="check-field"><input id="labels-checkbox" type="checkbox" /> <span>Подписи значений</span></label>
              <label class="check-field"><input id="reverse-checkbox" type="checkbox" /> <span>Обратить палитру</span></label>
            </div>
          </details>

          <details id="advanced-visual-settings">
            <summary>Расширенные настройки</summary>
            <div class="form-section">
              <label class="field chart-setting">
                <span>Ориентация</span>
                <select id="orientation-select">
                  <option value="auto">Авто</option>
                  <option value="horizontal">Горизонтальная</option>
                  <option value="vertical">Вертикальная</option>
                </select>
              </label>
              <label class="field chart-setting">
                <span>Сортировка</span>
                <select id="sort-order-select">
                  <option value="auto">Авто</option>
                  <option value="desc">По убыванию</option>
                  <option value="asc">По возрастанию</option>
                  <option value="none">Исходный порядок</option>
                </select>
              </label>
              <label class="field chart-setting">
                <span>Top N</span>
                <input id="top-n-input" type="number" min="0" max="100" value="0" />
                <small class="field-help">0 — показывать все значения.</small>
              </label>
              <label class="field chart-setting">
                <span>Шкала оси</span>
                <select id="axis-scale-select">
                  <option value="auto">Авто</option>
                  <option value="linear">Линейная</option>
                  <option value="log">Логарифмическая</option>
                </select>
              </label>
              <label class="field chart-setting">
                <span>Нулевая линия</span>
                <select id="include-zero-select">
                  <option value="auto">Авто</option>
                  <option value="always">Всегда включать ноль</option>
                  <option value="never">Не принуждать к нулю</option>
                </select>
              </label>
              <label class="field chart-setting">
                <span>Масштабирование</span>
                <select id="data-zoom-select">
                  <option value="auto">Авто</option>
                  <option value="always">Всегда</option>
                  <option value="never">Отключить</option>
                </select>
              </label>
              <label class="field chart-setting">
                <span>Маркеры точек</span>
                <select id="show-points-select">
                  <option value="auto">Авто</option>
                  <option value="always">Показывать</option>
                  <option value="never">Скрыть</option>
                </select>
              </label>
              <label class="field">
                <span>Положение легенды</span>
                <select id="legend-position-select">
                  <option value="auto">Авто</option>
                  <option value="top">Сверху</option>
                  <option value="bottom">Снизу</option>
                  <option value="right">Справа</option>
                </select>
              </label>
              <label class="check-field chart-setting"><input id="smooth-checkbox" type="checkbox" /> <span>Сглаживать линии</span></label>
              <label class="check-field"><input id="patterns-checkbox" type="checkbox" /> <span>Дублировать цвета узорами</span></label>
            </div>
          </details>

          <details id="type-specific-settings">
            <summary>Настройки виджета</summary>
            <div class="form-section">
              <label class="field generic-setting">
                <span>Формат числа</span>
                <select id="number-format-select">
                  <option value="auto">Авто</option>
                  <option value="plain">Обычный</option>
                  <option value="compact">Компактный</option>
                </select>
              </label>
              <label class="field generic-setting">
                <span>Режим подписей</span>
                <select id="label-mode-select">
                  <option value="auto">Авто</option>
                  <option value="none">Без подписей</option>
                  <option value="value">Значение</option>
                  <option value="percent">Доля</option>
                  <option value="value_percent">Значение + доля</option>
                  <option value="name_value">Название + значение</option>
                </select>
              </label>
              <label class="check-field generic-setting"><input id="show-unit-checkbox" type="checkbox" checked /> <span>Показывать единицу</span></label>
              <label class="check-field generic-setting"><input id="tooltip-checkbox" type="checkbox" checked /> <span>Tooltip при наведении</span></label>
              <label class="check-field generic-setting chart-only-setting"><input id="grid-checkbox" type="checkbox" checked /> <span>Сетка графика</span></label>
              <label class="field generic-setting">
                <span>Анимация</span>
                <select id="animation-select">
                  <option value="auto">Авто</option>
                  <option value="on">Включить</option>
                  <option value="off">Отключить</option>
                </select>
              </label>

              <label class="field line-area-setting"><span>Толщина линии</span><input id="line-width-input" type="number" min="1" max="8" step="0.5" /></label>
              <label class="field line-area-setting">
                <span>Тип линии</span>
                <select id="line-style-select"><option value="solid">Сплошная</option><option value="dashed">Пунктир</option><option value="dotted">Точки</option></select>
              </label>
              <label class="field line-area-setting">
                <span>Кривая</span>
                <select id="line-curve-select"><option value="linear">Линейная</option><option value="smooth">Сглаженная</option><option value="step-start">Ступень: начало</option><option value="step-middle">Ступень: середина</option><option value="step-end">Ступень: конец</option></select>
              </label>
              <label class="check-field line-area-setting"><input id="connect-nulls-checkbox" type="checkbox" /> <span>Соединять пропуски</span></label>
              <label class="field line-area-setting"><span>Непрозрачность area</span><input id="area-opacity-input" type="number" min="0" max="1" step="0.05" /></label>
              <label class="field line-area-setting">
                <span>Заливка area</span>
                <select id="area-fill-mode-select"><option value="gradient">Градиент</option><option value="solid">Сплошная</option></select>
              </label>
              <label class="field axis-x-limit-setting"><span>Минимум X</span><input id="x-axis-min-input" type="number" step="any" /></label>
              <label class="field axis-x-limit-setting"><span>Максимум X</span><input id="x-axis-max-input" type="number" step="any" /></label>
              <label class="field axis-limit-setting"><span>Минимум Y</span><input id="axis-min-input" type="number" step="any" /></label>
              <label class="field axis-limit-setting"><span>Максимум Y</span><input id="axis-max-input" type="number" step="any" /></label>

              <label class="field bar-setting">
                <span>Столбцы</span>
                <select id="bar-layout-select"><option value="grouped">Группы</option><option value="stacked">Стек</option><option value="percent">100%</option></select>
              </label>
              <label class="field bar-setting"><span>Ширина столбца</span><input id="bar-width-input" type="number" min="0" max="80" /></label>
              <label class="field bar-setting"><span>Зазор, %</span><input id="bar-gap-input" type="number" min="0" max="80" /></label>
              <label class="field bar-setting">
                <span>Позиция подписей</span>
                <select id="bar-label-position-select"><option value="auto">Авто</option><option value="inside">Внутри</option><option value="outside">Снаружи</option><option value="top">Сверху</option><option value="right">Справа</option></select>
              </label>
              <label class="check-field bar-setting"><input id="total-labels-checkbox" type="checkbox" /> <span>Итоговые подписи</span></label>
              <label class="field bar-pie-setting"><span>Мелкие в «Прочее», %</span><input id="other-threshold-input" type="number" min="0" max="20" step="0.5" /></label>

              <label class="field pie-setting">
                <span>Тип диаграммы</span>
                <select id="pie-variant-select"><option value="pie">Круг</option><option value="donut">Donut</option></select>
              </label>
              <label class="field pie-setting pie-radius-setting"><span>Внутренний радиус, %</span><input id="pie-inner-radius-input" type="number" min="1" max="80" /></label>
              <label class="field pie-setting"><span>Внешний радиус, %</span><input id="pie-outer-radius-input" type="number" min="30" max="90" /></label>
              <label class="field pie-setting">
                <span>Подписи pie</span>
                <select id="pie-label-mode-select"><option value="auto">Авто</option><option value="none">Нет</option><option value="outside">Снаружи</option><option value="inside">Внутри</option><option value="legend">Только легенда</option></select>
              </label>
              <label class="field pie-setting"><span>Угол старта, °</span><input id="pie-start-angle-input" type="number" min="0" max="360" /></label>
              <label class="field pie-setting">
                <span>Rose mode</span>
                <select id="pie-rose-mode-select"><option value="none">Нет</option><option value="radius">Radius</option><option value="area">Area</option></select>
              </label>

              <label class="check-field heatmap-setting"><input id="heatmap-transpose-checkbox" type="checkbox" /> <span>Транспонировать</span></label>
              <label class="field heatmap-setting">
                <span>Шкала heatmap</span>
                <select id="heatmap-scale-mode-select"><option value="auto">Авто</option><option value="global">Все значения</option><option value="fixed">Фиксированная</option></select>
              </label>
              <label class="field heatmap-setting"><span>Минимум heatmap</span><input id="heatmap-fixed-min-input" type="number" step="any" /></label>
              <label class="field heatmap-setting"><span>Максимум heatmap</span><input id="heatmap-fixed-max-input" type="number" step="any" /></label>
              <label class="field heatmap-setting"><span>Цвет пропусков</span><input id="heatmap-missing-color-input" type="color" value="#e2e8f0" /></label>
              <label class="field heatmap-setting"><span>Зазор ячейки</span><input id="heatmap-cell-gap-input" type="number" min="0" max="8" /></label>
              <label class="check-field heatmap-setting"><input id="heatmap-cell-border-checkbox" type="checkbox" checked /> <span>Границы ячеек</span></label>

              <label class="field pyramid-setting"><span>Sex dimension</span><input id="pyramid-sex-dimension-input" placeholder="grup_2" /></label>
              <label class="field pyramid-setting"><span>Age dimension</span><input id="pyramid-age-dimension-input" placeholder="vozr" /></label>
              <label class="field pyramid-setting"><span>Мужчины: value</span><input id="pyramid-male-value-input" placeholder="male / муж" /></label>
              <label class="field pyramid-setting"><span>Женщины: value</span><input id="pyramid-female-value-input" placeholder="female / жен" /></label>
              <label class="field pyramid-setting">
                <span>Режим</span>
                <select id="pyramid-mode-select"><option value="absolute">Абсолютный</option><option value="percent">Проценты</option></select>
              </label>
              <label class="field pyramid-setting"><span>Цвет мужчин</span><input id="pyramid-male-color-input" type="color" value="#2563eb" /></label>
              <label class="field pyramid-setting"><span>Цвет женщин</span><input id="pyramid-female-color-input" type="color" value="#f472b6" /></label>

              <label class="field kpi-setting">
                <span>Агрегация KPI</span>
                <select id="kpi-aggregation-select"><option value="latest">Последнее</option><option value="sum">Сумма</option><option value="mean">Среднее</option><option value="min">Минимум</option><option value="max">Максимум</option></select>
              </label>
              <label class="field kpi-setting">
                <span>Сравнение KPI</span>
                <select id="kpi-comparison-select"><option value="previous">Предыдущий период</option><option value="first">Первый период</option><option value="target">Цель</option><option value="none">Нет</option></select>
              </label>
              <label class="field kpi-setting"><span>Цель KPI</span><input id="kpi-target-input" type="number" step="any" /></label>
              <label class="check-field kpi-setting"><input id="kpi-sparkline-checkbox" type="checkbox" checked /> <span>Мини-график</span></label>
              <label class="field kpi-setting">
                <span>Стиль мини-графика</span>
                <select id="kpi-sparkline-style-select"><option value="line">Line</option><option value="area">Area</option><option value="bar">Bar</option></select>
              </label>
              <label class="field kpi-setting">
                <span>Иконка KPI</span>
                <select id="kpi-icon-mode-select"><option value="none">Без иконки</option><option value="auto">Авто</option><option value="custom">Своя</option></select>
              </label>
              <div class="field kpi-icon-picker-field kpi-setting full-width" id="kpi-icon-picker-field">
                <span>Выберите иконку</span>
                <div id="kpi-icon-picker" class="kpi-icon-picker" role="listbox" aria-label="Иконка KPI">
                  ${renderKpiIconChoices()}
                </div>
              </div>
              <label class="field kpi-setting kpi-icon-custom-field full-width" id="kpi-icon-custom-field">
                <span>Другое имя Lucide</span>
                <input id="kpi-icon-input" placeholder="activity" autocomplete="off" />
              </label>

              <label class="field table-setting">
                <span>Колонки таблицы</span>
                <select id="table-columns-select"><option value="auto">Авто</option><option value="compact">Компактно</option><option value="full">Полные</option></select>
              </label>
              <label class="field table-setting">
                <span>Плотность</span>
                <select id="table-density-select"><option value="comfortable">Свободно</option><option value="compact">Компактно</option><option value="dense">Плотно</option></select>
              </label>
              <label class="field table-setting"><span>Строк на страницу</span><input id="table-page-size-input" type="number" min="5" max="100" /></label>
              <label class="field table-setting">
                <span>Ячейки значений</span>
                <select id="table-cell-visual-select"><option value="none">Текст</option><option value="bars">Полосы</option><option value="heatmap">Heatmap</option><option value="font_scale">Цвет текста</option></select>
              </label>
              <label class="check-field table-setting"><input id="table-quality-checkbox" type="checkbox" checked /> <span>Качество данных</span></label>
            </div>
          </details>

          <details id="map-settings">
            <summary>Классификация и время карты</summary>
            <div class="form-section">
              <label class="field">
                <span>Метод</span>
                <select id="classification-select">
                  <option value="quantile">Квантили</option>
                  <option value="equal">Равные интервалы</option>
                  <option value="jenks">Естественные границы Jenks</option>
                  <option value="manual">Ручные границы</option>
                </select>
              </label>
              <label class="field">
                <span>Число классов</span>
                <input id="class-count-input" type="number" min="2" max="9" value="5" />
              </label>
              <label class="field full-width" id="manual-breaks-field" hidden>
                <span>Границы классов</span>
                <input id="manual-breaks-input" placeholder="Например: 5; 10; 15; 20" />
              </label>
              <label class="field full-width">
                <span>Сопоставимость шкалы во времени</span>
                <select id="map-scale-mode-select">
                  <option value="global">Единая для всех выбранных периодов</option>
                  <option value="current">Пересчитывать для текущего периода</option>
                  <option value="fixed">Фиксированный диапазон</option>
                </select>
                <small class="field-help">Единая шкала позволяет корректно сравнивать цвета между годами.</small>
              </label>
              <label class="field full-width">
                <span>Легенда карты</span>
                <select id="map-legend-mode-select">
                  <option value="collapsed">Кнопка, раскрывать по запросу</option>
                  <option value="open">Открыта по умолчанию</option>
                  <option value="hidden">Скрыта</option>
                </select>
              </label>
              <label class="field full-width">
                <span>Подложка карты</span>
                <select id="map-basemap-select">
                  <option value="auto">Авто: белая / тёмная</option>
                  <option value="blankLight">Светлая карта</option>
                  <option value="blankDark">Тёмная карта</option>
                  <option value="osm">OpenStreetMap</option>
                  <option value="satellite">Спутник</option>
                </select>
              </label>
              <label class="field map-fixed-field" hidden>
                <span>Минимум шкалы</span>
                <input id="map-fixed-min-input" type="number" step="any" />
              </label>
              <label class="field map-fixed-field" hidden>
                <span>Максимум шкалы</span>
                <input id="map-fixed-max-input" type="number" step="any" />
              </label>
              <label class="field">
                <span>Цвет пропусков</span>
                <input id="null-color-input" type="color" value="#e2e8f0" />
              </label>
              <label class="field map-style-setting">
                <span>Непрозрачность полигонов</span>
                <input id="map-fill-opacity-input" type="number" min="0.1" max="1" step="0.05" />
              </label>
              <label class="field map-style-setting">
                <span>Цвет границ</span>
                <input id="map-outline-color-input" type="color" value="#ffffff" />
              </label>
              <label class="field map-style-setting">
                <span>Толщина границ</span>
                <input id="map-outline-width-input" type="number" min="0" max="4" step="0.1" />
              </label>
              <label class="field map-style-setting">
                <span>Непрозрачность подложки</span>
                <input id="map-basemap-opacity-input" type="number" min="0" max="1" step="0.05" />
              </label>
              <label class="field symbol-map-setting">
                <span>Мин. радиус символа</span>
                <input id="map-symbol-min-radius-input" type="number" min="1" max="30" />
              </label>
              <label class="field symbol-map-setting">
                <span>Макс. радиус символа</span>
                <input id="map-symbol-max-radius-input" type="number" min="2" max="80" />
              </label>
              <label class="field symbol-map-setting">
                <span>Шкала символов</span>
                <select id="map-symbol-scale-select">
                  <option value="sqrt">Площадь</option>
                  <option value="linear">Линейная</option>
                </select>
              </label>
              <label class="field symbol-map-setting">
                <span>Обводка символа</span>
                <input id="map-symbol-stroke-color-input" type="color" value="#ffffff" />
              </label>
              <label class="field symbol-map-setting">
                <span>Толщина обводки символа</span>
                <input id="map-symbol-stroke-width-input" type="number" min="0" max="6" step="0.1" />
              </label>
              <label class="field symbol-map-setting">
                <span>Непрозрачность символа</span>
                <input id="map-symbol-opacity-input" type="number" min="0.1" max="1" step="0.05" />
              </label>
              <label class="check-field full-width"><input id="time-control-checkbox" type="checkbox" checked /> <span>Показывать селектор периода в виджете</span></label>
            </div>
          </details>

          <details>
            <summary>Условное оформление</summary>
            <div class="form-section">
              <label class="field">
                <span>Условие</span>
                <select id="condition-operator">
                  <option value="none">Не применять</option>
                  <option value="gt">Больше</option>
                  <option value="gte">Больше или равно</option>
                  <option value="lt">Меньше</option>
                  <option value="lte">Меньше или равно</option>
                </select>
              </label>
              <label class="field">
                <span>Порог</span>
                <input id="condition-value" type="number" step="any" disabled />
              </label>
              <label class="field">
                <span>Цвет</span>
                <input id="condition-color" type="color" value="#dc2626" disabled />
              </label>
            </div>
          </details>
        </div>
      </aside>

      <button id="builder-backdrop" class="builder-backdrop" type="button" aria-label="Закрыть панель конструктора"></button>

      <section class="canvas-panel">
        <div class="canvas-header app-internal-state" hidden>
          <h2 id="canvas-title" data-i18n-skip>Новый дашборд</h2>
          <div id="layout-help" class="layout-help"></div>
          <button id="toggle-layout" class="icon-button layout-mode-button" type="button" aria-pressed="true">${icon('move')}</button>
        </div>
        <div id="dashboard-filterbar" class="dashboard-filterbar" hidden>
          <label><span id="scope-filter-label">Территория</span><select id="scope-filter"></select></label>
          <button id="reset-scope-filter" class="button button-secondary" type="button">По умолчанию</button>
          <small id="scope-filter-note">Выберите территорию — запросы всех виджетов перестроятся под неё.</small>
        </div>
        <div class="canvas-scroll">
          <div id="dashboard-scale-shell" class="dashboard-scale-shell">
            <div id="dashboard-scale-inner" class="dashboard-scale-inner">
              <div id="empty-state" class="empty-state">
                <div class="empty-state-icon">＋</div>
                <h3>Пустой дашборд</h3>
                <p>Выберите показатель, территории и тип визуализации, затем добавьте первый виджет.</p>
                <div class="empty-state-actions">
                  <button id="empty-add-widget" class="button button-primary requires-sign-in" type="button">${icon('square-plus')}<span>Добавить виджет</span></button>
                  <button id="empty-auto-builder" class="button button-secondary requires-sign-in" type="button">${icon('sparkles')}<span>Автоконструктор</span></button>
                  <button id="empty-dashboard-manager" class="button button-secondary" type="button">${icon('layout-dashboard')}<span>Дашборды</span></button>
                </div>
              </div>
              <div id="dashboard-grid" class="grid-stack" aria-live="polite"></div>
            </div>
          </div>
        </div>
        <footer class="statusbar">
          <span id="global-status">Инициализация…</span>
          <span class="statusbar-right">
            <span id="layout-status">Сетка: 12 колонок</span>
            <span id="widget-counter">0 виджетов</span>
          </span>
        </footer>
      </section>
    </main>
  </div>

  <select id="dashboard-select" class="app-internal-state" aria-label="Выбор дашборда" hidden></select>

  <dialog id="dashboard-manager-dialog" class="dashboard-manager-dialog" aria-labelledby="dashboard-manager-title">
    <div class="dashboard-manager-shell">
      <header class="dashboard-manager-header">
        <div>
          <p class="eyebrow">Дашборды</p>
          <h2 id="dashboard-manager-title">Сохранённые дашборды</h2>
        </div>
        <button id="dashboard-manager-close" class="icon-button" type="button" aria-label="Закрыть менеджер">${icon('x')}</button>
      </header>
      <div class="dashboard-manager-tools">
        <label class="dashboard-manager-search">
          ${icon('search')}
          <input id="dashboard-manager-search" type="search" placeholder="Поиск по названию" autocomplete="off" />
        </label>
        <button id="dashboard-manager-new" class="button button-secondary requires-sign-in" type="button">${icon('file-plus-2')}<span>Новый</span></button>
      </div>
      <div class="dashboard-manager-tabs" role="tablist" aria-label="Разделы дашбордов">
        <button id="dashboard-manager-tab-mine" class="dashboard-manager-tab" type="button" role="tab" data-dashboard-tab="mine" aria-controls="dashboard-manager-list" aria-selected="true">Мои</button>
        <button id="dashboard-manager-tab-shared" class="dashboard-manager-tab" type="button" role="tab" data-dashboard-tab="shared" aria-controls="dashboard-manager-list" aria-selected="false" tabindex="-1">Общие</button>
        <button id="dashboard-manager-tab-all" class="dashboard-manager-tab" type="button" role="tab" data-dashboard-tab="all" aria-controls="dashboard-manager-list" aria-selected="false" tabindex="-1" hidden>Все</button>
      </div>
      <p id="dashboard-manager-demo-hint" class="dashboard-manager-hint" hidden></p>
      <div id="dashboard-manager-list" class="dashboard-manager-list" role="tabpanel" aria-live="polite"></div>
    </div>
  </dialog>

  <dialog id="auto-builder-dialog" class="auto-builder-dialog" aria-labelledby="auto-builder-title">
    <div class="auto-builder-shell">
      <header class="auto-builder-header">
        <div>
          <h2 id="auto-builder-title">Автоконструктор дашборда</h2>
          <p>Выберите источник и раздел: платформа соберёт новую витрину из разных показателей и визуализаций.</p>
        </div>
        <button id="auto-builder-close" class="icon-button" type="button" aria-label="Закрыть автоконструктор">${icon('x')}</button>
      </header>
      <div class="auto-builder-grid">
        <label class="field">
          <span>Источник</span>
          <select id="auto-source-select">
            <option value="bdmo">Муниципальная статистика</option>
            <option value="worldbank" hidden disabled>Международная статистика</option>
            <option value="emiss">Региональная статистика</option>
            <option value="settlements">Населённые пункты</option>
          </select>
        </label>
        <label class="field">
          <span>Раздел</span>
          <input id="auto-section-search" type="search" autocomplete="off" placeholder="Поиск раздела" />
          <select id="auto-section-select"><option value="">Все разделы</option></select>
        </label>
        <section class="auto-builder-card full-width" aria-labelledby="auto-indicators-title">
          <div class="auto-builder-card-head">
            <div>
              <h3 id="auto-indicators-title">Показатели</h3>
              <small id="auto-indicator-counter">Автоподбор</small>
            </div>
            <label class="inline-check">
              <input id="auto-pick-enabled" type="checkbox" checked />
              <span>Автоподбор</span>
            </label>
            <label class="inline-check">
              <input id="auto-fill-missing" type="checkbox" checked />
              <span>Автодобор</span>
            </label>
          </div>
          <input id="auto-indicator-search" class="auto-search-input" type="search" autocomplete="off" placeholder="Поиск показателя по названию или ID" />
          <div id="auto-indicator-choice-list" class="checkbox-choice-list auto-choice-list" aria-label="Выбор показателей"></div>
          <small class="field-help">Если ничего не отмечено, автоконструктор сам подберёт разные показатели из раздела.</small>
        </section>
        <section id="auto-bdmo-territory-panel" class="auto-builder-card full-width" hidden aria-labelledby="auto-bdmo-territory-title">
          <div class="auto-builder-card-head">
            <div>
              <h3 id="auto-bdmo-territory-title">Территории</h3>
              <small id="auto-bdmo-territory-counter">Автовыбор</small>
            </div>
          </div>
          <div class="auto-cascade-grid">
            <div class="auto-cascade-column">
              <div class="auto-list-head">
                <span>Федеральные округа</span>
                <span id="auto-district-counter">0</span>
              </div>
              <input id="auto-district-search" class="auto-search-input" type="search" autocomplete="off" placeholder="Поиск округа" />
              <div class="auto-list-actions">
                <button id="auto-district-select-all" type="button" class="text-button">Выбрать все</button>
                <button id="auto-district-clear" type="button" class="text-button">Снять выбор</button>
              </div>
              <select id="auto-district-select" multiple hidden></select>
              <div id="auto-district-choice-list" class="checkbox-choice-list auto-choice-list compact" aria-label="Выбор федеральных округов"></div>
            </div>
            <div class="auto-cascade-column">
              <div class="auto-list-head">
                <span>Субъекты РФ</span>
                <span id="auto-region-counter">0</span>
              </div>
              <input id="auto-region-search" class="auto-search-input" type="search" autocomplete="off" placeholder="Поиск субъекта" />
              <div class="auto-list-actions">
                <button id="auto-region-select-all" type="button" class="text-button">Выбрать все</button>
                <button id="auto-region-clear" type="button" class="text-button">Снять выбор</button>
              </div>
              <select id="auto-region-select" multiple hidden></select>
              <div id="auto-region-choice-list" class="checkbox-choice-list auto-choice-list compact" aria-label="Выбор субъектов РФ"></div>
            </div>
            <div class="auto-cascade-column">
              <div class="auto-list-head">
                <span>Муниципалитеты</span>
                <span id="auto-municipality-counter">0</span>
              </div>
              <input id="auto-municipality-search" class="auto-search-input" type="search" autocomplete="off" placeholder="Поиск муниципалитета" />
              <div class="auto-list-actions">
                <button id="auto-municipality-select-all" type="button" class="text-button">Выбрать все</button>
                <button id="auto-municipality-clear" type="button" class="text-button">Снять выбор</button>
              </div>
              <select id="auto-municipality-select" multiple hidden></select>
              <div id="auto-municipality-choice-list" class="checkbox-choice-list auto-choice-list compact" aria-label="Выбор муниципалитетов"></div>
            </div>
          </div>
          <small class="field-help">Пустой выбор на уровне означает “не ограничивать”; муниципалитеты фильтруются выбранными округами и субъектами.</small>
        </section>
        <section id="auto-generic-territory-panel" class="auto-builder-card full-width" aria-labelledby="auto-territory-title">
          <div class="auto-builder-card-head">
            <div>
              <h3 id="auto-territory-title">Территории</h3>
              <small id="auto-territory-counter">Автовыбор</small>
            </div>
          </div>
          <input id="auto-territory-search" class="auto-search-input" type="search" autocomplete="off" placeholder="Поиск территории" />
          <div class="auto-list-actions">
            <button id="auto-territory-select-all" type="button" class="text-button">Выбрать все</button>
            <button id="auto-territory-clear" type="button" class="text-button">Снять выбор</button>
          </div>
          <select id="auto-territory-select" multiple hidden></select>
          <div id="auto-territory-choice-list" class="checkbox-choice-list auto-choice-list" aria-label="Выбор территорий"></div>
          <small class="field-help">Пустой выбор означает все доступные территории в безопасном лимите.</small>
        </section>
        <section class="auto-builder-card full-width" aria-labelledby="auto-years-title">
          <div class="auto-builder-card-head">
            <div>
              <h3 id="auto-years-title">Годы</h3>
              <small id="auto-year-counter">Автовыбор</small>
            </div>
          </div>
          <input id="auto-year-search" class="auto-search-input" type="search" autocomplete="off" placeholder="Поиск года" />
          <div class="auto-list-actions">
            <button id="auto-year-select-all" type="button" class="text-button">Выбрать все</button>
            <button id="auto-year-clear" type="button" class="text-button">Снять выбор</button>
          </div>
          <select id="auto-year-select" multiple hidden></select>
          <div id="auto-year-choice-list" class="checkbox-choice-list auto-choice-list compact" aria-label="Выбор годов"></div>
          <small id="auto-year-help" class="field-help">Пустой выбор означает все доступные годы выбранных показателей.</small>
        </section>
        <section class="auto-builder-card" aria-labelledby="auto-color-title">
          <div class="auto-builder-card-head">
            <div>
              <h3 id="auto-color-title">Цвет страницы</h3>
              <small>Единый цвет витрины</small>
            </div>
          </div>
          <div class="auto-color-row">
            <input id="auto-primary-color" type="color" value="${initialPreferences.lastPrimaryColor}" />
            <button id="auto-color-reset" type="button" class="button button-secondary">Сбросить</button>
          </div>
          <div id="auto-color-presets" class="settings-presets auto-color-presets" aria-label="Рекомендованные цвета"></div>
        </section>
      </div>
      <div id="auto-builder-status" class="auto-builder-status" role="status">Готов к созданию новой витрины.</div>
      <div class="auto-builder-actions">
        <button id="auto-builder-cancel" class="button button-secondary" type="button">Отмена</button>
        <button id="auto-builder-create" class="button button-accent" type="button">${icon('sparkles')}<span>Создать дашборд</span></button>
      </div>
    </div>
  </dialog>

  <dialog id="emiss-dialog" class="emiss-dialog" aria-labelledby="emiss-dialog-title">
    <div class="emiss-dialog-shell">
      <header class="emiss-dialog-header">
        <div>
          <div class="eyebrow">Удалённый источник</div>
          <h2 id="emiss-dialog-title">Менеджер ЕМИСС / fedstat.ru</h2>
          <p>Проверка метаданных, настройка фильтров и регистрация показателя без предварительной выгрузки всей базы.</p>
        </div>
        <button id="close-emiss-manager" class="icon-button" type="button" aria-label="Закрыть менеджер ЕМИСС">×</button>
      </header>
      <div class="emiss-connection-strip">
        <span id="emiss-connection-badge" class="source-status-badge">Проверка конфигурации…</span>
        <span id="emiss-connection-description"></span>
      </div>
      <div class="emiss-manager-grid">
        <section class="emiss-manager-primary">
          <section class="emiss-catalog-search" aria-labelledby="emiss-catalog-title">
            <div class="emiss-catalog-heading">
              <div>
                <h3 id="emiss-catalog-title">Каталог показателей fedstat.ru</h3>
                <p id="emiss-catalog-status">Каталог ещё не загружен.</p>
              </div>
              <button id="emiss-catalog-refresh" class="button button-secondary" type="button">Обновить каталог</button>
            </div>
            <label class="field full-width">
              <span>Поиск показателя по названию или ID</span>
              <div class="emiss-inline-search emiss-catalog-search-row">
                <input id="emiss-catalog-query" type="search" placeholder="Например: рождаемость или 62852" autocomplete="off" />
                <button id="emiss-catalog-search-button" class="button button-primary" type="button">Найти</button>
              </div>
            </label>
            <label class="check-field emiss-catalog-toggle">
              <input id="emiss-catalog-include-excluded" type="checkbox" />
              <span>Показать скрытые/исключённые</span>
            </label>
            <div id="emiss-catalog-results" class="emiss-catalog-results" aria-live="polite"></div>
          </section>
          <div class="emiss-form-grid">
            <label class="field">
              <span>ID показателя ЕМИСС</span>
              <input id="emiss-indicator-id" inputmode="numeric" pattern="[0-9]{1,12}" value="31517" placeholder="Например, 31517" />
            </label>
            <label class="field">
              <span>Локальный ID</span>
              <input id="emiss-local-id" placeholder="Сформируется автоматически" />
            </label>
            <label class="field full-width">
              <span>Название в каталоге</span>
              <input id="emiss-name" placeholder="Будет взято из ЕМИСС" />
            </label>
            <label class="field">
              <span>Единица измерения</span>
              <input id="emiss-unit" value="ед." />
            </label>
            <label class="check-field emiss-force-field"><input id="emiss-force-refresh" type="checkbox" /> <span>Обновить метаданные принудительно</span></label>
            <label class="check-field"><input id="emiss-active" type="checkbox" checked /> <span>Активировать после проверки</span></label>
            <label class="check-field"><input id="emiss-overwrite" type="checkbox" /> <span>Перезаписать существующую регистрацию</span></label>
          </div>
          <div class="emiss-actions">
            <button id="emiss-diagnostics" class="button button-secondary" type="button">Проверить доступ</button>
            <button id="emiss-inspect" class="button button-primary" type="button">Исследовать показатель</button>
            <button id="emiss-register" class="button button-accent" type="button" disabled>Зарегистрировать</button>
          </div>
          <div id="emiss-manager-message" class="emiss-manager-message" role="status">Введите ID показателя и выполните исследование метаданных.</div>
          <div id="emiss-inspection-summary" class="emiss-inspection-summary"></div>
          <div id="emiss-filter-list" class="emiss-filter-list"></div>
        </section>
        <aside class="emiss-manager-secondary">
          <div class="emiss-secondary-heading">
            <h3>Последние обращения</h3>
            <button id="refresh-emiss-log" class="icon-button" type="button" title="Обновить журнал" aria-label="Обновить журнал">↺</button>
          </div>
          <div id="emiss-request-log" class="emiss-request-log"><p>Журнал ещё не загружен.</p></div>
          <div class="emiss-security-note">
            <strong>Сетевой маршрут не хранится в дашборде.</strong> Приложение не хранит сетевые параметры и не поднимает туннелей: при работе через прокси бэкенду передаётся только его адрес, ключевой материал остаётся вне репозитория.
          </div>
        </aside>
      </div>
    </div>
  </dialog>

  <div class="settings-dock" id="settings-dock">
    <section id="settings-popover" class="settings-popover" aria-labelledby="settings-title" hidden>
      <div class="settings-heading">
        <strong id="settings-title">${t(initialPreferences.locale, 'settings')}</strong>
        <button id="settings-reset-colors" class="text-button" type="button">${t(initialPreferences.locale, 'resetColors')}</button>
      </div>
      <label class="settings-row">
        <span>${t(initialPreferences.locale, 'language')}</span>
        <span class="segmented-control" role="group" aria-label="${t(initialPreferences.locale, 'language')}">
          <button id="settings-locale-ru" type="button" data-locale-choice="ru">RU</button>
          <button id="settings-locale-en" type="button" data-locale-choice="en">EN</button>
        </span>
      </label>
      <label class="settings-row">
        <span>${t(initialPreferences.locale, 'theme')}</span>
        <span class="segmented-control theme-control" role="group" aria-label="${t(initialPreferences.locale, 'theme')}">
          <button id="settings-theme-system" type="button" data-theme-choice="system">${t(initialPreferences.locale, 'system')}</button>
          <button id="settings-theme-light" type="button" data-theme-choice="light">${t(initialPreferences.locale, 'light')}</button>
          <button id="settings-theme-dark" type="button" data-theme-choice="dark">${t(initialPreferences.locale, 'dark')}</button>
        </span>
      </label>
      <div class="settings-colors">
        <label>
          <span>${t(initialPreferences.locale, 'primaryColor')}</span>
          <input id="settings-primary-color" type="color" value="${initialPreferences.lastPrimaryColor}" />
        </label>
      </div>
      <div class="settings-presets" id="settings-presets" aria-label="${t(initialPreferences.locale, 'presets')}"></div>
      <p id="settings-color-warning" class="settings-warning" hidden>${t(initialPreferences.locale, 'colorWarning')}</p>
    </section>
  </div>
`;
  mounted = true;
}
